// src/seo/Seo.jsx
import React from 'react';
import { Helmet } from 'react-helmet';
import {
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
  getRouteMeta,
} from './siteMeta';

/**
 * Jeden komponent SEO dla wszystkich podstron.
 *
 * <Seo path="/cennik" jsonLd={[productJsonLd()]} />
 * albo z nadpisaniem: <Seo path="/cennik" title="..." description="..." />
 */
const Seo = ({
  path,
  title,
  description,
  image = DEFAULT_OG_IMAGE,
  robots,
  type = 'website',
  jsonLd = [],
  children,
}) => {
  const fallback = getRouteMeta(path);
  const finalTitle = title || fallback.title;
  const finalDescription = description || fallback.description;
  const finalRobots = robots || fallback.robots || 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
  const canonical = absoluteUrl(fallback.canonicalPath || path);
  const blocks = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : [jsonLd].filter(Boolean);

  // Tytuł w SERP-ach: marka na końcu, o ile nie jest już w tytule.
  const brandedTitle = /pilotuje/i.test(finalTitle) ? finalTitle : `${finalTitle} | ${SITE_NAME}`;

  return (
    <Helmet prioritizeSeoTags>
      <html lang="pl" />
      <title>{brandedTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="robots" content={finalRobots} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph - wpływa na CTR przy udostępnianiu i w Discoverze */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="pl_PL" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={brandedTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={brandedTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={image} />

      {blocks.map((block, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(block)}
        </script>
      ))}

      {children}
    </Helmet>
  );
};

export default Seo;
